/**
 * Scrollable message list. Renders user + assistant turns; streaming
 * bubbles fade-update as tokens arrive.
 *
 * Source references:
 *   - Loveable v1 reference: `src/components/XaesAnalyst.tsx` lines
 *     287-390 — same conceptual layout, ported to shadcn + lucide and
 *     this rebuild's typography scale.
 */

import { useEffect, useRef } from "react";
import { motion } from "motion/react";
import { AtSign, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PlasmaCanvas } from "@/components/fx/PlasmaCanvas";
import { cn } from "@/lib/utils";
import type { ChatMessage, ChatStatus } from "../use-chat";
import { AssistantMarkdown } from "./AssistantMarkdown";
import { Sources } from "./Sources";

type Props = {
  messages: ChatMessage[];
  status: ChatStatus;
  error: string | null;
  configured: boolean;
  onRetry: () => void;
};

export function ChatMessages({
  messages,
  status,
  error,
  configured,
  onRetry,
}: Props) {
  const endRef = useRef<HTMLDivElement | null>(null);
  const scrollerRef = useRef<HTMLDivElement | null>(null);

  // Auto-scroll to the latest message only when the user is already
  // near the bottom — preserves scroll position when reading history.
  useEffect(() => {
    const sc = scrollerRef.current;
    if (!sc) return;
    const distanceFromBottom = sc.scrollHeight - sc.clientHeight - sc.scrollTop;
    if (distanceFromBottom < 80) {
      endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [messages]);

  if (messages.length === 0) {
    /* Empty-state welcome — mirrors the legacy `#welcomeMessage` block
       in `_legacy/wd.html` line 634: a soft sky-tinted halo behind the
       plasma orbit canvas, then a muted "ASK XAES" eyebrow. The
       PlasmaCanvas FX is the same one mounted in `XaesAgentModal`. */
    return (
      <div className="pointer-events-none flex flex-1 flex-col items-center justify-center gap-1 px-4 text-center">
        <div className="relative inline-flex items-center justify-center">
          <div
            aria-hidden
            className="absolute"
            style={{
              width: 90,
              height: 90,
              borderRadius: "50%",
              background:
                "radial-gradient(circle, rgba(var(--accent-sky-rgb), 0.10) 0%, rgba(var(--accent-sky-rgb), 0) 70%)",
              filter: "blur(14px)",
            }}
          />
          <PlasmaCanvas
            className="relative block opacity-90"
            ariaLabel="XAES Analyst plasma"
            // Match legacy `chatPlasmaCanvas` dimensions (`_legacy/wd.html`
            // line 637 — `width:120; height:66`).
            style={{ width: 120, height: 66 }}
          />
        </div>
        <span
          className="font-sans font-light uppercase"
          style={{
            color: "var(--text-muted)",
            fontSize: "var(--fs-base)",
            letterSpacing: "0.5px",
            marginTop: 6,
          }}
        >
          Ask XAES
        </span>
        {!configured && (
          <div className="border-gold/40 bg-gold/10 text-gold pointer-events-auto mt-2 flex items-center gap-1.5 rounded-sm border px-2 py-1 font-mono text-fs-label tracking-[0.12em] uppercase">
            <span>Mock mode — no API key</span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      ref={scrollerRef}
      role="log"
      aria-label="XAES Analyst conversation"
      className="flex-1 min-h-0 overflow-y-auto pr-1"
    >
      <ul className="flex flex-col gap-3 py-2">
        {messages.map((msg) => (
          <MessageBubble key={msg.id} msg={msg} />
        ))}
        {error && status === "error" && (
          <li>
            <div className="border-destructive/40 bg-destructive/5 text-destructive flex items-center justify-between gap-2 rounded-md border px-2.5 py-2 text-xs">
              <span className="truncate">{error}</span>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={onRetry}
                className="text-destructive hover:text-destructive h-6 gap-1 px-2 text-fs-label tracking-[0.12em] uppercase"
              >
                <RefreshCw className="h-3 w-3" strokeWidth={2} aria-hidden />
                Retry
              </Button>
            </div>
          </li>
        )}
        <div ref={endRef} />
      </ul>
    </div>
  );
}

function MessageBubble({ msg }: { msg: ChatMessage }) {
  const isUser = msg.role === "user";

  return (
    <li
      className={cn(
        "flex gap-2",
        isUser ? "flex-row-reverse" : "flex-row",
      )}
    >
      <motion.div
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
        className={cn(
          "max-w-[85%] min-w-0 rounded-md border px-2.5 py-1.5",
          isUser
            ? "border-border/40 bg-secondary/60"
            : "border-true-blue/30 bg-true-blue/5",
          msg.error && "border-destructive/40 bg-destructive/5",
        )}
      >
        {!isUser && (
          <div className="text-true-blue mb-0.5 font-mono text-fs-label tracking-[0.14em] uppercase">
            XAES Analyst
          </div>
        )}
        {isUser ? (
          <span className="text-foreground/90 block whitespace-pre-wrap text-sm leading-relaxed">
            {msg.text}
          </span>
        ) : (
          <div className="text-foreground/90 min-w-0">
            {msg.text ? (
              <AssistantMarkdown text={msg.text} />
            ) : (
              msg.streaming && (
                <span
                  className="bg-true-blue inline-block h-3 w-1.5 animate-pulse align-middle"
                  aria-hidden
                />
              )
            )}
            {msg.streaming && msg.text && (
              <span
                className="bg-true-blue ml-0.5 inline-block h-3 w-1.5 animate-pulse align-middle"
                aria-hidden
              />
            )}
          </div>
        )}
        {msg.references && msg.references.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1">
            {msg.references.map((r) => (
              <span
                key={`${r.kind}-${r.workshopId}-${r.phase ?? "x"}`}
                className="border-true-blue/40 bg-true-blue/10 text-true-blue inline-flex items-center gap-1 rounded-sm border px-1 py-0.5 font-mono text-fs-micro tracking-[0.08em]"
              >
                <AtSign className="h-2 w-2" strokeWidth={2} aria-hidden />
                <span>{r.label}</span>
              </span>
            ))}
          </div>
        )}
        {!isUser &&
          msg.citedIndices !== undefined &&
          (msg.grounding || (msg.sources && msg.sources.length > 0)) && (
            <Sources
              sources={msg.sources ?? []}
              citedIndices={msg.citedIndices}
              grounding={msg.grounding}
            />
          )}
      </motion.div>
    </li>
  );
}
